/**
 * Convert any color format (hex, rgb, rgba, hsl, hsla) to OKLCH
 * @param {string} color - Color in hex, rgb, rgba, hsl, or hsla format
 * @returns {{l: string, c: string, h: string, a?: string}} OKLCH components
 */
export const toOKLCH = (color) => {
  if (!color) return null;
  // Helper: Parse color string to RGB [0-255]
  const parseColor = (colorString) => {
    const str = colorString.trim().toLowerCase();

    // HEX format (#RGB or #RRGGBB or #RRGGBBAA)
    if (str.startsWith("#")) {
      const hex = str.slice(1);
      let r,
        g,
        b,
        a = 1;

      if (hex.length === 3 || hex.length === 4) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
        if (hex.length === 4) a = parseInt(hex[3] + hex[3], 16) / 255;
      } else if (hex.length === 6 || hex.length === 8) {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
        if (hex.length === 8) a = parseInt(hex.slice(6, 8), 16) / 255;
      }

      return {r, g, b, a};
    }

    // RGB/RGBA format
    const rgbMatch = str.match(
      /rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/
    );
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1]),
        g: parseInt(rgbMatch[2]),
        b: parseInt(rgbMatch[3]),
        a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
      };
    }

    // HSL/HSLA format
    const hslMatch = str.match(
      /hsla?\s*\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*([\d.]+)\s*)?\)/
    );
    if (hslMatch) {
      const h = parseFloat(hslMatch[1]) / 360;
      const s = parseFloat(hslMatch[2]) / 100;
      const l = parseFloat(hslMatch[3]) / 100;
      const a = hslMatch[4] ? parseFloat(hslMatch[4]) : 1;

      // Convert HSL to RGB
      const hslToRgb = (h, s, l) => {
        let r, g, b;

        if (s === 0) {
          r = g = b = l;
        } else {
          const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
          };

          const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
          const p = 2 * l - q;
          r = hue2rgb(p, q, h + 1 / 3);
          g = hue2rgb(p, q, h);
          b = hue2rgb(p, q, h - 1 / 3);
        }

        return {
          r: Math.round(r * 255),
          g: Math.round(g * 255),
          b: Math.round(b * 255),
          a,
        };
      };

      return hslToRgb(h, s, l);
    }

    throw new Error(`Unable to parse color: ${colorString}`);
  };

  // Helper: Convert sRGB to linear RGB
  const srgbToLinear = (c) => {
    const normalized = c / 255;
    return normalized <= 0.04045 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  // Helper: Linear RGB to XYZ (D65 illuminant)
  const rgbToXyz = (r, g, b) => {
    const rL = srgbToLinear(r);
    const gL = srgbToLinear(g);
    const bL = srgbToLinear(b);

    const x = 0.4124564 * rL + 0.3575761 * gL + 0.1804375 * bL;
    const y = 0.2126729 * rL + 0.7151522 * gL + 0.072175 * bL;
    const z = 0.0193339 * rL + 0.119192 * gL + 0.9503041 * bL;

    return {x, y, z};
  };

  // Helper: XYZ to OKLab
  const xyzToOklab = (x, y, z) => {
    const l = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z;
    const m = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z;
    const s = 0.0482003018 * x + 0.2643662691 * y + 0.633851707 * z;

    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);

    return {
      L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
      a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
      b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
    };
  };

  // Helper: OKLab to OKLCH
  const oklabToOklch = (L, a, b) => {
    const C = Math.sqrt(a * a + b * b);
    let H = Math.atan2(b, a) * (180 / Math.PI);
    if (H < 0) H += 360;

    return {L, C, H};
  };

  // Main conversion
  const {r, g, b, a} = parseColor(color);
  const {x, y, z} = rgbToXyz(r, g, b);
  const {L, a: labA, b: labB} = xyzToOklab(x, y, z);
  const {L: lightness, C: chroma, H: hue} = oklabToOklch(L, labA, labB);

  // Format output
  const l = lightness.toFixed(3);
  const c = chroma.toFixed(3);
  const h = hue.toFixed(1);

  // Include alpha if not fully opaque
  if (a < 1) {
    return {l, c, h, a: a.toFixed(3)};
  }

  return {l, c, h};
};
